import React, { MouseEvent, useEffect } from 'react';
import { CardContent, Typography } from '@mui/material';
import { useStyles } from '../UrbanSportsConnect/ConnectUrbanSports.styles';
import { KeyboardArrowRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useUSCConnectContext } from '../UrbanSportsConnect/UrbanSportsConnectContext';
import { KeyboardDatePicker } from '~/UIKit';
import { IIntegration, IntegrationValues } from '~/Models/DTO/Integration.model';
import Clear from '@mui/icons-material/Clear';
import { PLAIN_GREY } from '~/Constants/Colors.constants';

interface IdListCardContentProps {
  ActiveIntegrations: IIntegration[] | undefined;
}

export const IdListCardContent = ({ ActiveIntegrations }: IdListCardContentProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const {
    dates,
    setDates,
    selectedClient,
    setSelectedClient,
    selectedIntegrationId,
    setSelectedIntegrationId,
    setAnchorEl,
    setShowRemovalModal,
  } = useUSCConnectContext();

  const { startDate, endDate, initialStartDate, initialEndDate } = dates;

  useEffect(() => {
    if (ActiveIntegrations && selectedClient) {
      const integration = ActiveIntegrations.find((int) => int.clientName === selectedClient);
      if (integration && selectedIntegrationId !== integration.clientId) {
        setSelectedIntegrationId(integration.clientId);
      }
    } else if (ActiveIntegrations && ActiveIntegrations.length > 0 && !selectedClient) {
      setSelectedIntegrationId(ActiveIntegrations[0].clientId);
      setSelectedClient(ActiveIntegrations[0].clientName);
    }
  }, [
    ActiveIntegrations,
    selectedClient,
    selectedIntegrationId,
    setSelectedIntegrationId,
    setSelectedClient,
  ]);

  const startDateDisabled = initialStartDate !== '' && initialStartDate !== null;
  const startDateValue = startDate !== '' ? startDate : startDateDisabled ? initialStartDate : null;
  const endDateValue =
    endDate !== ''
      ? endDate
      : initialEndDate !== '' && initialEndDate !== null
      ? initialEndDate
      : null;

  const handleOpenPopover = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleDateChange = (name: string, date: any) => {
    if (name === IntegrationValues.StartDate) {
      setDates((prev) => ({ ...prev, startDate: date }));
    } else {
      setDates((prev) => ({ ...prev, endDate: date }));
    }
  };

  const clearEndDate = () => {
    setDates((prev) => ({ ...prev, endDate: '' }));
    setDates((prev) => ({ ...prev, initialEndDate: '' }));
  };

  return (
    <CardContent>
      <Typography variant="h5" className={classes.heading}>
        {t('select.an.integration')}
      </Typography>
      <Typography variant="body2" className={classes.description}>
        {t('usc.connect.select.description')}
      </Typography>
      <div className={`${classes.inputRoot}`}>
        <Typography variant="body2" className={classes.selectLabel}>
          {t('select.integration')}
        </Typography>
        <div className={classes.selectedItems}>
          {selectedClient && (
            <Typography className={classes.selectedItem}>{selectedClient}</Typography>
          )}
          {selectedClient ? (
            <div className={classes.selectItemIcon}>
              <Clear
                className={`${classes.arrowIcon} ${classes.crossIcon} ${classes.cursorPointer}`}
                onClick={() => {
                  setShowRemovalModal(true);
                  setSelectedClient('');
                  setSelectedIntegrationId('');
                }}
              />
            </div>
          ) : (
            <div className={classes.selectItemIcon} onClick={handleOpenPopover}>
              <KeyboardArrowRight className={`${classes.arrowIcon} ${classes.cursorPointer}`} />
            </div>
          )}
        </div>
      </div>
      <div className={`${classes.inputRoot}`}>
        <KeyboardDatePicker
          label={t('magicline.setup.start.date')}
          name={IntegrationValues.StartDate}
          onDateChange={handleDateChange}
          options={{ labelBold: true }}
          data-testid="startdate"
          disabled={startDateDisabled}
          value={startDateValue}
        />
      </div>
      <div className={`${classes.inputRoot} ${classes.uscConnectDateInput}`}>
        <KeyboardDatePicker
          label={t('magicline.setup.end.date')}
          name={IntegrationValues.EndDate}
          onDateChange={handleDateChange}
          options={{
            labelBold: true,
            Icon: (
              <Clear
                style={{
                  color: PLAIN_GREY,
                  zIndex: '-1',
                  visibility: 'hidden',
                  border: 'none',
                  outline: 'none',
                }}
                onClick={clearEndDate}
              />
            ),
          }}
          value={endDateValue}
        />
        {(endDate !== '' || (initialEndDate !== null && initialEndDate !== '')) && (
          <div className={`${classes.removeDate} ${classes.clearRightSpace}`}>
            <Clear className="endDateCrossIcon" onClick={clearEndDate} />
          </div>
        )}
      </div>
    </CardContent>
  );
};
